import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

export const payrollRunStateV1Schema = z.enum([
  "Draft",
  "Readiness Review",
  "Ready for Submission",
  "Submitted",
  "Processing",
  "Failed",
  "Final",
  "Reconciliation Required",
  "Reconciled",
])

export const payrollRunInputReferenceV1Schema = z
  .object({
    inputId: entityIdSchema,
    inputVersion: z.number().int().positive(),
  })
  .strict()

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const locationCodeSchema = z.string().regex(/^[A-Z0-9-]{1,12}$/)

export const payrollRunCreateV1Schema = z
  .object({
    runId: entityIdSchema,
    periodStart: dateSchema,
    periodEnd: dateSchema,
    jurisdictionPackId: entityIdSchema,
    jurisdictionPackVersion: z.number().int().positive(),
    countryCode: z.string().regex(/^[A-Z]{2}$/),
    stateCode: locationCodeSchema,
    localityCode: locationCodeSchema,
    inputReferences: z.array(payrollRunInputReferenceV1Schema).min(1).max(250),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.periodStart > value.periodEnd) {
      context.addIssue({
        code: "custom",
        path: ["periodEnd"],
        message: "Payroll period must be increasing.",
      })
    }
    const ids = value.inputReferences.map((item) => item.inputId)
    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: "custom",
        path: ["inputReferences"],
        message: "Payroll inputs must be unique.",
      })
    }
  })

export const payrollRunTransitionV1Schema = z
  .object({
    runId: entityIdSchema,
    targetState: payrollRunStateV1Schema,
    reason: z.string().trim().min(1).max(500),
  })
  .strict()

export const payrollRunResultV1Schema = z
  .object({
    entityType: z.literal("payroll_run"),
    entityId: entityIdSchema,
    state: payrollRunStateV1Schema,
    version: z.number().int().positive(),
    legalEntityId: entityIdSchema,
    periodStart: dateSchema,
    periodEnd: dateSchema,
    jurisdictionPackId: entityIdSchema,
    jurisdictionPackVersion: z.number().int().positive(),
    inputCount: z.number().int().positive(),
    inputVersionsLocked: z.literal(true),
    supportedJurisdiction: z.literal(true),
  })
  .strict()

export type PayrollRunCreateV1 = z.infer<typeof payrollRunCreateV1Schema>
export type PayrollRunTransitionV1 = z.infer<typeof payrollRunTransitionV1Schema>
export type PayrollRunResultV1 = z.infer<typeof payrollRunResultV1Schema>
